import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './api';
import type { IntegrationSummary, IntegrationType } from '@ventin/shared';

const KEY = ['integrations'] as const;

export function useIntegrations() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => apiFetch<{ integrations: IntegrationSummary[] }>('/api/admin/integrations'),
  });
}

export function useUpsertIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { type: IntegrationType; payload: Record<string, unknown> }) =>
      apiFetch<{ integration: IntegrationSummary }>(`/api/admin/integrations/${input.type}`, {
        method: 'PUT',
        body: input.payload,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useTestIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (type: IntegrationType) =>
      apiFetch<{ integration: IntegrationSummary; test: { ok: boolean; error: string | null } }>(
        `/api/admin/integrations/${type}/test`,
        { method: 'POST' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (type: IntegrationType) =>
      apiFetch<void>(`/api/admin/integrations/${type}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * BLOCKER-2: GA4 OAuth start is POST (authenticated fetch carries Bearer),
 * returning JSON { authorizeUrl, state }. Caller should then set
 * window.location.href = authorizeUrl so the browser navigates to Google
 * without needing to carry an Authorization header.
 */
export function useStartGa4Oauth() {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ authorizeUrl: string; state: string }>('/api/admin/integrations/ga4/oauth/start', {
        method: 'POST',
      }),
  });
}
